import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { take, timer } from 'rxjs';

import moment from 'moment';
import { TreoMediaWatcherService } from 'tsui/@treo';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import {
  Calendar as FullCalendar,
  CalendarOptions,
  EventApi,
  EventClickArg,
  EventMountArg,
  EventSourceInput,
} from '@fullcalendar/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import momentPlugin from '@fullcalendar/moment';
import rrulePlugin from '@fullcalendar/rrule';
import timeGridPlugin from '@fullcalendar/timegrid';
import { CalendarService } from 'app/modules/calendar/calendar.service';
import { Calendar, CalendarDrawerMode, CalendarEvent, CalendarSettings } from 'app/modules/calendar/calendar.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { User } from 'app/core/models/user.types';
import { UserService } from 'app/layout/common/user/user.service';
import { CalendarFilterComponent } from 'app/modules/calendar/calendar-filter/calendar-filter.component';
import UrlUtils from 'app/shared/Utils/url.utils';
import { environment } from '@env/environment';

@Component({
  selector: 'calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CalendarComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  private calendars: Calendar[];
  private eventTimeFormat: {
    hour: string;
    hour12: boolean;
    minute: string;
    meridiem: boolean | string;
  };
  private events: Array<any> = [];
  private settings: CalendarSettings;
  private views: { [viewId: string]: object };
  private user: User;
  private data: Data;
  private queryFilters: { assignedUserIdIn?: number[]; objectTypeIn?: string[] };
  private eventPanelOverlayRef: OverlayRef;
  private eventPanelTemplatePortal: TemplatePortal;
  private fullCalendarApi: FullCalendar;

  @ViewChild('eventPanel') private eventPanel: TemplateRef<any>;
  @ViewChild('fullCalendar') private fullCalendar: FullCalendarComponent;
  @ViewChild('drawer') private drawer: MatDrawer;

  public drawerMode: CalendarDrawerMode = 'side';
  public drawerOpened: boolean = true;
  public event: CalendarEvent;
  public view: 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listYear' = 'dayGridMonth';
  public calendarTitle: string;
  public calendarOptions: CalendarOptions;
  public isEventsLoading: boolean = false;

  constructor(
    private readonly _treoMediaWatcherService: TreoMediaWatcherService,
    private readonly _calendarService: CalendarService,
    private readonly _projectsService: ProjectsService,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _matDialog: MatDialog,
    private readonly clipboard: Clipboard,
    private readonly _userService: UserService,
    private readonly _snackBar: MatSnackBar,
    private readonly _overlay: Overlay,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _activatedRoute: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getRouteData();
    this.fillQueryFilters();
    this.getPipelines();
    this.fetchCalendarEvents();
    this.getSettings();
    this.mediaChangesWatcher();
    this.buildCalendarViews();
    this.getUsers();
    this.buildCalendarOptions();
  }

  ngAfterViewInit(): void {
    this.getEvents();
  }

  private getRouteData(): void {
    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data) => (this.data = data));
  }

  private saveQueryFilters(filters: any): void {
    UrlUtils.saveToLocalStorage('calendarViewFilters', filters, true);
  }

  private fillQueryFilters(): void {
    this.queryFilters = UrlUtils.getFromLocalStorage('calendarViewFilters', true);
  }

  private getPipelines(): void {
    this._projectsService
      .getPipelines(true)
      .pipe(untilComponentDestroyed(this))
      .subscribe((pipelines) => {
        if (pipelines) {
          this.calendars = pipelines.map((pipeline) => {
            return {
              id: pipeline.id,
              title: pipeline.name,
              visible: true,
              color: pipeline.color || 'gray',
            };
          });
          this._cdRef.markForCheck();
        }
      });
  }

  private fetchCalendarEvents(): void {
    this._calendarService.events$.pipe(untilComponentDestroyed(this)).subscribe((events) => {
      if (events && this.calendarOptions) this.calendarOptions.events = events as EventSourceInput;
      this._cdRef.markForCheck();
    });
  }

  private getSettings(): void {
    this._calendarService.settings$.pipe(untilComponentDestroyed(this)).subscribe((settings) => {
      this.settings = settings;
      this.eventTimeFormat = {
        hour: settings.timeFormat === '12' ? 'numeric' : '2-digit',
        hour12: settings.timeFormat === '12',
        minute: '2-digit',
        meridiem: settings.timeFormat === '12' ? 'short' : false,
      };
      this._cdRef.markForCheck();
    });
  }

  private mediaChangesWatcher(): void {
    this._treoMediaWatcherService.onMediaChange$
      .pipe(untilComponentDestroyed(this))
      .subscribe(({ matchingAliases }) => {
        if (matchingAliases.includes('lt-md')) {
          this.drawerMode = 'over';
          this.drawerOpened = false;
        } else {
          this.drawerMode = 'side';
          this.drawerOpened = true;
        }
        this._cdRef.markForCheck();
      });
  }

  private buildCalendarViews(): void {
    this.views = {
      dayGridMonth: {
        dayMaxEventRows: 3,
        eventTimeFormat: this.eventTimeFormat,
        fixedWeekCount: false,
      },
      timeGrid: {
        allDayContent: '',
        dayHeaderFormat: {
          weekday: 'short',
          day: 'numeric',
          omitCommas: true,
        },
        dayHeaderContent: (args) => {
          return {
            html: `
              <span class="fc-weekday">${moment(args.date).format('ddd')}</span>
              <span class="fc-date">${moment(args.date).format('D')}</span>
            `,
          };
        },
        slotDuration: '01:00:00',
        slotLabelFormat: this.eventTimeFormat,
      },
      listYear: {
        allDayContent: 'All day',
        eventTimeFormat: this.eventTimeFormat,
        listDayFormat: false,
        listDaySideFormat: false,
      },
    };
  }

  private getUsers(): void {
    this._userService.user$.pipe(untilComponentDestroyed(this)).subscribe((user: User) => {
      this.user = user;
      this._cdRef.markForCheck();
    });
  }

  private buildCalendarOptions(): CalendarOptions {
    return (this.calendarOptions = {
      initialView: 'dayGridMonth',
      views: this.views,
      contentHeight: '100%',
      headerToolbar: false,
      handleWindowResize: true,
      firstDay: this.settings?.startWeekOn,
      eventClick: this.onEventClick.bind(this),
      eventsSet: this.handleEvents.bind(this),
      eventDidMount: this.onEventRender.bind(this),
      events: this.events,
      plugins: [dayGridPlugin, interactionPlugin, listPlugin, momentPlugin, rrulePlugin, timeGridPlugin],
    });
  }

  private getEvents(): void {
    this.isEventsLoading = true;
    this.fullCalendarApi = this.fullCalendar.getApi();
    this.calendarTitle = this.fullCalendarApi.view.title;

    const viewStart = moment(this.fullCalendarApi.view.currentStart);
    const viewEnd = moment(this.fullCalendarApi.view.currentEnd);

    this._calendarService
      .getEvents(viewStart, viewEnd, true, this.queryFilters)
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: () => {
          this.fullCalendarApi.refetchEvents();
        },
        complete: () => {
          this.isEventsLoading = false;
          this._cdRef.detectChanges();
        },
      });
  }

  private openEventPanel(calendarEvent: EventClickArg): void {
    this.eventPanelOverlayRef = this._overlay.create({
      panelClass: ['calendar-event-panel', 'panel-mode-view'],
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(calendarEvent.el)
        .withFlexibleDimensions(false)
        .withPositions([
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 8,
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'top',
            offsetX: -8,
          },
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'bottom',
            offsetX: -8,
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetX: 8,
          },
        ]),
    });

    this.eventPanelTemplatePortal = new TemplatePortal(this.eventPanel, this._viewContainerRef);

    this.eventPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.closeEventPanel());

    this.eventPanelOverlayRef.attach(this.eventPanelTemplatePortal);
    this._cdRef.markForCheck();
  }

  private closeEventPanel(): void {
    if (this.eventPanelTemplatePortal && this.eventPanelTemplatePortal.isAttached)
      this.eventPanelTemplatePortal.detach();

    if (this.eventPanelOverlayRef && this.eventPanelOverlayRef.hasAttached()) {
      this.eventPanelOverlayRef.detach();
      this.eventPanelOverlayRef.dispose();
    }

    this._cdRef.markForCheck();
  }

  private onEventClick(eventClick: EventClickArg): void {
    const event: CalendarEvent = this.events.find(
      (item) => parseInt(item.id.toString()) === parseInt(eventClick.event.id),
    );
    if (event) {
      this.event = event;
      // Prepare the end value
      let end;
      // If this is a recurring event...
      if (event?.recurringEventId) {
        // Calculate the end value using the duration
        end = eventClick.event.end;
      } else {
        // Set the end value from the end
        end = event.end;
      }

      // Set the range on the event
      event.range = {
        start: event.start,
        end,
      };
      this.openEventPanel(eventClick);
    }
  }

  private handleEvents(events: EventApi[]): void {
    this.events = events;
    this._cdRef.detectChanges();
  }

  private onEventRender(eventMount: EventMountArg): void {
    const calendar = this.calendars.find((item) => item.id === eventMount.event.extendedProps.calendarId);

    if (!calendar) return;

    if (this.view === 'listYear') {
      // Create a new 'fc-list-item-date' node that represents the event date in the listYear calendar view.
      const listYearDateColumn = `
        <td class="fc-list-item-date">
          <span>
              <span>${moment(eventMount.event.start).format('D')}</span>
              <span>
                ${moment(eventMount.event.start).format('MMM')},
                ${moment(eventMount.event.start).format('ddd')}
              </span>
            </span>
        </td>
      `;

      // Insert the 'fc-list-item-date' into the calendar event element
      eventMount.el.insertAdjacentHTML('afterbegin', listYearDateColumn);
    }

    // Set the color class of the event
    eventMount.event.setProp('backgroundColor', calendar.color);
    eventMount.event.setProp('borderColor', calendar.color);

    // Set the event's title to '(No title)' if event title is not available
    if (!eventMount.event.title) eventMount.event.setProp('title', '(No title)');
  }

  public openFilterDialog(): void {
    this._matDialog
      .open(CalendarFilterComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { data: this.data, filters: this.queryFilters },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.filters) {
          this.queryFilters = result.filters;
          this.saveQueryFilters(this.queryFilters);
          this.getEvents();
          this._cdRef.markForCheck();
        }
      });
  }

  public copyIcalUrl(): void {
    let icalUrl = `${environment.baseUrl}/calendar?uuid=${this.user.uuid}`;
    this.clipboard.copy(icalUrl);
    this._snackBar.open('Calendar url copied to clipboard.', '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  public toggleDrawer(): void {
    this.drawer.toggle();
    const closeAfterAnimation$ = timer(300);
    closeAfterAnimation$.pipe(take(1)).subscribe(() => this.fullCalendarApi.updateSize());
  }

  public getEventExtendedProps(event): any {
    return event ? event.extendedProps : null;
  }

  public getCalendar(event): Calendar {
    return event ? this.calendars.find((calendar) => calendar.id === event.extendedProps.calendarId) : null;
  }

  public changeView(view: 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listYear'): void {
    this.view = view;
    if (this.fullCalendarApi) {
      this.fullCalendarApi.changeView(view);
      this.calendarTitle = this.fullCalendarApi.view.title;
      this.getEvents();
    }
  }

  public navigateCalendarDates(command: 'prev' | 'today' | 'next'): void {
    this.fullCalendarApi[command]();
    this.calendarTitle = this.fullCalendarApi.view.title;
    this.getEvents();
    return;
  }

  public onCalendarToggled(calendar: Calendar): void {
    const toggledCalendar = this.events.filter((item) => item.extendedProps.calendarId === calendar?.id);
    toggledCalendar.forEach((event) => event.setProp('display', !calendar.visible ? 'none' : 'block'));
    this._cdRef.detectChanges();
  }

  public onCalendarEdited(calendar: Calendar) {
    const editedEvents = this.events.filter((item) => item.extendedProps.calendarId === calendar.id);

    editedEvents.forEach((event: EventApi) => {
      if (event) {
        this.getCalendar(event).title = calendar.title;
        event.setProp('backgroundColor', calendar.color);
        event.setProp('borderColor', calendar.color);
      }
    });
    this._cdRef.detectChanges();
  }
}
